import {
  AssignmentType,
  assignmentTypeLabel,
} from '@app/features/contracts/embauche-dialog/embauche-tabs-form/assignment-form/assignment-type.model';
import { DAYS_OF_WEEK, DayOfWeek, Schedule } from '@app/features/horaires/schedules.model';
import { residencyLabel } from '@app/features/residences/residency-label';
import { Residency } from '@app/features/residences/residency.model';
import { slotsByDay } from '@app/features/horaires/slots-label';
import { Amendment } from './amendment.model';

/** Une ligne « champ, avant, après » de la vue des modifications. */
export interface FieldChange {
  field: string;
  before: string;
  after: string;
}

/** Des jours consécutifs dont les créneaux changent de la même façon. */
export interface ScheduleChange {
  days: string;
  before: string;
  after: string;
}

export interface AssignmentChange {
  /** Le type d'affectation, s'il change. */
  type: { before: string; after: string } | null;
  kept: string[];
  added: string[];
  removed: string[];
}

/** Ce que l'avenant change, groupé par thème : le reste ne se montre pas (RG-avenant-consultation). */
export interface AmendmentChanges {
  workingTime: FieldChange[];
  schedules: ScheduleChange[];
  assignment: AssignmentChange | null;
}

const DAY_LABELS: Record<DayOfWeek, string> = {
  MONDAY: 'Lundi',
  TUESDAY: 'Mardi',
  WEDNESDAY: 'Mercredi',
  THURSDAY: 'Jeudi',
  FRIDAY: 'Vendredi',
  SATURDAY: 'Samedi',
  SUNDAY: 'Dimanche',
};

/** Les heures par mois d'une durée hebdomadaire, comme l'API les compte : 52 semaines par an. */
const monthly = (weekly: number) => (weekly * 52) / 12;

const hours = (value: number) =>
  `${value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} h`;

export function amendmentChanges(amendment: Amendment): AmendmentChanges {
  const previous = amendment.previousConditions;
  return {
    workingTime: workingTimeChanges(previous.weeklyWorkingHours, amendment.weeklyWorkingHours),
    schedules: scheduleChanges(previous.schedule.schedules, amendment.schedule.schedules),
    assignment: assignmentChange(
      previous.assignmentType,
      previous.residencies,
      amendment.assignmentType,
      amendment.residencies,
    ),
  };
}

function workingTimeChanges(before: number, after: number): FieldChange[] {
  if (Number(before) === Number(after)) {
    return [];
  }
  return [
    { field: 'Durée hebdomadaire', before: hours(before), after: hours(after) },
    { field: 'Durée mensuelle', before: hours(monthly(before)), after: hours(monthly(after)) },
  ];
}

function scheduleChanges(
  before: Schedule[] | undefined,
  after: Schedule[] | undefined,
): ScheduleChange[] {
  const previous = slotsByDay(before);
  const next = slotsByDay(after);
  const changes: { from: DayOfWeek; to: DayOfWeek; before: string; after: string }[] = [];
  let last: DayOfWeek | null = null;
  for (const day of DAYS_OF_WEEK) {
    if (previous[day] !== next[day]) {
      const current = changes.at(-1);
      if (
        current &&
        last === current.to &&
        current.before === previous[day] &&
        current.after === next[day]
      ) {
        current.to = day;
      } else {
        changes.push({ from: day, to: day, before: previous[day], after: next[day] });
      }
      last = day;
    } else {
      last = null;
    }
  }
  return changes.map((change) => ({
    days:
      change.from === change.to
        ? DAY_LABELS[change.from]
        : `${DAY_LABELS[change.from]} → ${DAY_LABELS[change.to].toLowerCase()}`,
    before: change.before,
    after: change.after,
  }));
}

function assignmentChange(
  typeBefore: AssignmentType,
  residenciesBefore: Residency[],
  typeAfter: AssignmentType,
  residenciesAfter: Residency[],
): AssignmentChange | null {
  const before = typeBefore === 'SPECIFIC' ? residenciesBefore.map(residencyLabel) : [];
  const after = typeAfter === 'SPECIFIC' ? residenciesAfter.map(residencyLabel) : [];
  const added = after.filter((label) => !before.includes(label));
  const removed = before.filter((label) => !after.includes(label));
  if (typeBefore === typeAfter && added.length === 0 && removed.length === 0) {
    return null;
  }
  return {
    type:
      typeBefore === typeAfter
        ? null
        : { before: assignmentTypeLabel(typeBefore), after: assignmentTypeLabel(typeAfter) },
    kept: after.filter((label) => before.includes(label)),
    added,
    removed,
  };
}
